import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Paper from "@mui/material/Paper";
import { GridColDef } from "@mui/x-data-grid-pro";

import DataGridPro from "../../components/DataGridPro";
import Header from "../../components/Header";
import Page from "../../components/Page";
import RouteSelector from "../../components/RouteSelector";
import { useDispatch, useSelector } from "../../store";
import { useGetLogsQuery } from "../../store/pomeriumApi";
import { pushError } from "@/slices/notifications";

const POLL_INTERVAL_MS = 1000; // 1 seconds
const LOG_WINDOW_MINUTES = 5; // Last 5 minutes
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const Logs: FC = () => {
  const dispatch = useDispatch();
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getValidPageSize = (size: number): number => {
    return PAGE_SIZE_OPTIONS.includes(size) ? size : 100;
  };

  const [routeId, setRouteId] = useState(searchParams.get("routeId") || "");

  // Convert 1-based URL page to 0-based DataGrid page
  const urlPage = Number(searchParams.get("page") || "1");
  const initialPage = Math.max(0, urlPage - 1);

  const [paginationModel, setPaginationModel] = useState({
    page: initialPage,
    pageSize: getValidPageSize(Number(searchParams.get("pageSize") || "100")),
  });

  const handleRouteChange = (newRouteId: string | null) => {
    const normalizedRouteId = newRouteId || "";
    setRouteId(normalizedRouteId);

    const newParams = new URLSearchParams(window.location.search);
    if (normalizedRouteId) {
      newParams.set("routeId", normalizedRouteId);
    } else {
      newParams.delete("routeId");
    }
    newParams.set("page", "1");
    setSearchParams(newParams, { replace: true });

    setUniqueLogs([]);
    setSeenInsertIds(new Set());
    lastFetchTimeRef.current = null;
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (newModel: {
    page: number;
    pageSize: number;
  }) => {
    setPaginationModel(newModel);

    const newParams = new URLSearchParams(window.location.search);
    newParams.set("page", (newModel.page + 1).toString());
    newParams.set("pageSize", newModel.pageSize.toString());
    setSearchParams(newParams, { replace: true });
  };

  const [uniqueLogs, setUniqueLogs] = useState<any[]>([]);
  const [seenInsertIds, setSeenInsertIds] = useState<Set<string>>(new Set());
  const lastFetchTimeRef = useRef<Date | null>(null);
  const [queryTimestamp, setQueryTimestamp] = useState<Date>(() => new Date());
  const [isPaused, setIsPaused] = useState<boolean>(false);

  // Calculate time range for the query
  // Use queryTimestamp which only updates when we want to poll
  const { startTime, endTime } = useMemo(() => {
    const end = queryTimestamp;
    // Calculate start time:
    // - First request: fetch last 5 minutes
    // - Subsequent requests: fetch only since last fetch (incremental)
    const start = lastFetchTimeRef.current
      ? lastFetchTimeRef.current
      : new Date(end.getTime() - LOG_WINDOW_MINUTES * 60 * 1000);
    return { startTime: start, endTime: end };
  }, [queryTimestamp]);

  const {
    data: logs,
    isLoading,
    isFetching,
    error,
  } = useGetLogsQuery(
    {
      organizationId,
      clusterId,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      routeId: routeId || undefined,
    },
    {
      skip: !organizationId || !clusterId,
    },
  );

  // De-duplicate logs based on insertId and add to unique logs
  useEffect(() => {
    if (logs !== undefined && logs.length > 0) {
      const newUniqueLogs: any[] = [];
      const newSeenIds = new Set(seenInsertIds);

      logs.forEach((log) => {
        const insertId = log.insertId || "";
        // Only add if we haven't seen this insertId before
        if (insertId && !newSeenIds.has(insertId)) {
          newUniqueLogs.push(log);
          newSeenIds.add(insertId);
        } else if (!insertId) {
          // If no insertId, add it anyway (shouldn't happen with real data)
          newUniqueLogs.push(log);
        }
      });

      if (newUniqueLogs.length > 0) {
        setUniqueLogs((prev) => [...prev, ...newUniqueLogs]);
        setSeenInsertIds(newSeenIds);
      }

      // Update lastFetchTime to the most recent log timestamp
      // This ensures we don't miss any logs between queries
      const maxTimestamp = logs.reduce((max, log) => {
        const logTime = new Date(log.timestamp).getTime();
        return logTime > max ? logTime : max;
      }, 0);

      if (maxTimestamp > 0) {
        lastFetchTimeRef.current = new Date(maxTimestamp);
      }
    }
    // If no logs received, keep the same lastFetchTimeRef to query from the same point
  }, [logs]);

  // Update query timestamp for polling (only when previous request completed)
  useEffect(() => {
    if (!isPaused && !isFetching) {
      const intervalId = setInterval(() => {
        setQueryTimestamp(new Date());
      }, POLL_INTERVAL_MS);

      return () => clearInterval(intervalId);
    }
  }, [isPaused, isFetching]);

  // Show error toast when API request fails
  useEffect(() => {
    if (error) {
      dispatch(pushError("Error fetching logs"));
    }
  }, [error, dispatch]);

  // Transform unique logs array into rows with ids
  // Use insertId as the unique key if available, otherwise fall back to index
  const rows = uniqueLogs.map((log, index) => ({
    id: log.insertId || `log-${index}`,
    timestamp: log.timestamp,
    service: log.labels?.service || "",
    labels: log.labels || {},
  }));

  const columns: GridColDef[] = [
    {
      field: "timestamp",
      headerName: "Timestamp",
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {params.value ? new Date(params.value).toLocaleString() : ""}
        </Typography>
      ),
    },
    {
      field: "service",
      headerName: "Service",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          {params.value || "-"}
        </Typography>
      ),
    },
    {
      field: "labels",
      headerName: "Labels",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const labels = params.value as Record<string, string> | undefined;
        if (!labels || Object.keys(labels).length === 0) {
          return <Typography variant="body2">No labels</Typography>;
        }

        return (
          <Box sx={{ width: "100%", py: 1 }}>
            {Object.entries(labels).map(([key, value]) => (
              <Box
                key={key}
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 0.5,
                  fontFamily: "monospace",
                  fontSize: "0.875rem",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  }}
                >
                  {key}:
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                    wordBreak: "break-word",
                  }}
                >
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      },
    },
  ];

  // Mobile single-column layout
  const mobileColumns: GridColDef[] = [
    {
      field: "combined",
      headerName: "Log Entry",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const labels = row.labels as Record<string, string> | undefined;

        return (
          <Box
            sx={{
              width: "100%",
              py: 2,
              px: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            {/* Timestamp */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                Timestamp
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", mt: 0.5 }}
              >
                {row.timestamp ? new Date(row.timestamp).toLocaleString() : "-"}
              </Typography>
            </Box>

            {/* Service */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                Service
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", mt: 0.5 }}
              >
                {row.service || "-"}
              </Typography>
            </Box>

            {/* Labels */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: "bold",
                  color: "text.secondary",
                }}
              >
                Labels
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {!labels || Object.keys(labels).length === 0 ? (
                  <Typography variant="body2">No labels</Typography>
                ) : (
                  Object.entries(labels).map(([key, value]) => (
                    <Box
                      key={key}
                      sx={{
                        display: "flex",
                        gap: 1,
                        mb: 0.5,
                        fontFamily: "monospace",
                        fontSize: "0.875rem",
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                        }}
                      >
                        {key}:
                      </Typography>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.875rem",
                          wordBreak: "break-word",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        );
      },
    },
  ];

  return (
    <Page title="Logs">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 64px - 96px)",
          maxHeight: "calc(100vh - 64px - 96px)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Container maxWidth={false}>
            <Header title="Logs" />

            <Box
              sx={{
                mt: 2,
                mb: 2,
                display: "flex",
                alignItems: "flex-end",
                gap: 2,
              }}
            >
              <FormControl sx={{ maxWidth: 400, flex: 1 }}>
                <FormLabel sx={{ mb: 1 }}>Filter by Route</FormLabel>
                <RouteSelector
                  routeId={routeId}
                  onRouteIdChange={handleRouteChange}
                />
              </FormControl>
              <Button
                variant="contained"
                onClick={() => setIsPaused(!isPaused)}
                color={isPaused ? "secondary" : "primary"}
                sx={{
                  minWidth: "auto",
                  px: 2,
                  py: 1,
                }}
                aria-label={isPaused ? "Resume polling" : "Pause polling"}
              >
                {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
              </Button>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, mb: 2 }}
            >
              Showing {rows.length} unique log{rows.length !== 1 ? "s" : ""}{" "}
              (loaded last {LOG_WINDOW_MINUTES} minutes initially
              {isPaused
                ? ", polling paused"
                : `, polling every ${POLL_INTERVAL_MS / 1000} seconds for new entries`}
              ){routeId && " for selected route"}
            </Typography>
          </Container>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            px: 3,
            pb: 3,
          }}
        >
          <Paper
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <DataGridPro
              loading={isLoading}
              rows={rows}
              columns={isMobile ? mobileColumns : columns}
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
              checkboxSelection={false}
              sx={{
                border: 0,
                flex: 1,
                minHeight: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "background.default",
                  borderBottom: "2px solid",
                  borderColor: "divider",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-row": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                },
                "& .MuiDataGrid-row:nth-of-type(even)": {
                  backgroundColor: "action.hover",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "none",
                  py: isMobile ? 0 : 1,
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
              }}
              paginationMode="client"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </Paper>
        </Container>
      </Box>
    </Page>
  );
};

export default Logs;
